import MembershipType, { MEMBERSHIP_TIER_CONDITIONS_TERMS } from 'src/models/membership-type/MembershipType'
import Model, { encoders, ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/Model'

export const MEMBERSHIP_TIER_DESCRIPTION_TYPES = {
  PREFIX: 'prefix',
  REPLACE: 'replace'
}
MEMBERSHIP_TIER_DESCRIPTION_TYPES.ALL = _.values(MEMBERSHIP_TIER_DESCRIPTION_TYPES)

export const MEMBERSHIP_TIER_CONDITIONS = {
  VISITS: 'visits',
  TICKETS: 'tickets',
  MOVIES: 'movies',
  POINTS: 'points'
}
MEMBERSHIP_TIER_CONDITIONS.ALL = _.values(MEMBERSHIP_TIER_CONDITIONS)

export default class MembershipTier extends Model {
  static name = 'MembershipTier'

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Site]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      name: {
        type: String,
        sortable: true,
        translatable: true
      },
      membershipType: {
        type: MembershipType,
        encode: encoders.hasOne,
        reference: true,
        output: true
      },
      descriptionType: {
        type: String,
        default: 'prefix'
      },
      description: {
        type: String,
        default: '',
        translatable: true
      },
      priorityOrder: {
        type: Integer,
        default: 100
      },
      conditions: {
        type: JsonArray,
        default: () => ([]),
        required: false
      },
      conditionsRequirements: {
        type: JsonObject,
        default: () => ({
          visits: 1,
          tickets: 1,
          movies: 1,
          points: 1
        }),
        required: false
      },
      color: {
        type: String,
        default: '#027be3',
        required: false
      }
    }

    this.computed = {
      isConditional () {
        return !!this.conditions.length
      },
      requiresVisits () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.VISITS)
      },
      requiresTickets () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.TICKETS)
      },
      requiresMovies () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.MOVIES)
      },
      requiresPoints () {
        return _.includes(this.conditions, MEMBERSHIP_TIER_CONDITIONS.POINTS)
      },
      activeMembershipVouchers () {
        return _.filter(this.membershipType.activeMembershipVouchers, { tierId: this.id })
      },
      conditionalMembershipVouchers () {
        return _.filter(this.activeMembershipVouchers, benefit => { return benefit.conditions.length > 1 })
      },
      unconditionalMembershipVouchers () {
        return _.filter(this.activeMembershipVouchers, benefit => { return benefit.conditions.length === 1 })
      },
      unlimitedVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, { limitRedemptions: false })
      },
      unlimitedTicketVouchers () {
        return _.filter(this.unlimitedVouchers, 'isTicket')
      },
      hasUnlimitedTicketVouchers () {
        return !!this.unlimitedTicketVouchers.length
      },
      annualVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, 'isAnnual')
      },
      annualTicketVouchers () {
        return _.filter(this.annualVouchers, 'isTicket')
      },
      hasAnnualTicketVouchers () {
        return !!this.annualTicketVouchers.length
      },
      annualTicketVouchersCount () {
        return _.sumBy(this.annualTicketVouchers, 'redemptionsLimit')
      },
      monthlyVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, 'isMonthly')
      },
      monthlyTicketVouchers () {
        return _.filter(this.monthlyVouchers, 'isTicket')
      },
      hasMonthlyTicketVouchers () {
        return !!this.monthlyTicketVouchers.length
      },
      monthlyTicketVouchersCount () {
        return _.sumBy(this.monthlyTicketVouchers, 'redemptionsLimit')
      },
      activeMembershipDiscounts () {
        return _.filter(this.membershipType.activeMembershipDiscounts, { tierId: this.id })
      },
      conditionalMembershipDiscounts () {
        return _.filter(this.activeMembershipDiscounts, benefit => { return benefit.conditions.length > 1 })
      },
      unconditionalMembershipDiscounts () {
        return _.filter(this.activeMembershipDiscounts, benefit => { return benefit.conditions.length === 1 })
      },
      unlimitedDiscounts () {
        return _.filter(this.unconditionalMembershipDiscounts, { limitRedemptions: false })
      },
      hasUnlimitedDiscounts () {
        return !!this.unlimitedDiscounts.length
      },
      limitedDiscounts () {
        return _.filter(this.unconditionalMembershipDiscounts, 'limitRedemptions')
      },
      hasLimitedDiscounts () {
        return !!this.limitedDiscounts.length
      },
      activeMembershipPoints () {
        return _.filter(this.membershipType.activeMembershipPoints, { tierId: this.id })
      },
      conditionalPoints () {
        return _.filter(this.activeMembershipPoints, benefit => { return benefit.conditions.length > 1 })
      },
      unconditionalPoints () {
        return _.filter(this.activeMembershipPoints, benefit => { return benefit.conditions.length === 1 })
      },
      hasUnconditionalPoints () {
        return !!this.unconditionalPoints.length
      },
      unconditionalVisitPoints () {
        return _.sumBy(this.unconditionalPoints, 'visitPointsAccumulationRate')
      },
      unconditionalConcessionsPoints () {
        return _.sumBy(this.unconditionalPoints, 'concessionPointsAccumulationRate')
      },
      unconditionalTicketPricePoints () {
        return _.sumBy(this.unconditionalPoints, 'ticketPricePointsAccumulationRate')
      },
      unconditionalTicketValuePoints () {
        return _.sumBy(this.unconditionalPoints, 'ticketValuePointsAccumulationRate')
      },
      conditionsDescription () {
        const conditions = []
        if (this.isConditional) {
          if (this.requiresVisits) {
            conditions.push(`visit ${this.conditionsRequirements.visits} times`)
          }
          if (this.requiresTickets) {
            conditions.push(`buy ${this.conditionsRequirements.tickets} tickets`)
          }
          if (this.requiresMovies) {
            conditions.push(`see ${this.conditionsRequirements.movies} movies`)
          }
          if (this.requiresPoints) {
            conditions.push(`earn ${this.conditionsRequirements.points} points`)
          }
        }
        return _.startCase(conditions.join(', and')) + ` ${this.membershipType.conditionsTerm === MEMBERSHIP_TIER_CONDITIONS_TERMS.MONTHS ? 'for each month of your membership' : 'each year'} to get ${this.name} benefits!`
      },
      formattedConditions () {
        const conditions = _.map(this.conditions, condition => _.startCase(condition))

        if (this.requiresVisits) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.VISITS))
          conditions.push(`${this.conditionsRequirements.visits} Visits Each ${_.startCase(this.membershipType.conditionsTerm).slice(0, -1)}`)
        }
        if (this.requiresTickets) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.TICKETS))
          conditions.push(`${this.conditionsRequirements.tickets} Tickets Each ${_.startCase(this.membershipType.conditionsTerm).slice(0, -1)}`)
        }
        if (this.requiresMovies) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.MOVIES))
          conditions.push(`${this.conditionsRequirements.movies} Movies Each ${_.startCase(this.membershipType.conditionsTerm).slice(0, -1)}`)
        }
        if (this.requiresPoints) {
          _.remove(conditions, condition => condition === _.startCase(MEMBERSHIP_TIER_CONDITIONS.POINTS))
          conditions.push(`${this.conditionsRequirements.points} Points Each ${_.startCase(this.membershipType.conditionsTerm).slice(0, -1)}`)
        }

        return conditions
      }
    }
  }
}
